import React, { useState, useEffect, Suspense } from 'react';
import Navigation from './components/Navigation';
import Hub from './components/Hub';
import CommunityEchoes from './components/CommunityEchoes';
import Discover from './components/Discover';
import ExpertSpotlight from './components/ExpertSpotlight';
import Shop from './components/Shop';
import Profile from './components/Profile';
import AuthModal from './components/AuthModal';
import LandingPage from './components/LandingPage';
import ToastProvider from './components/Toast/ToastProvider';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import { NetworkStatusBar } from './components/StatusIndicators';
import { ConfirmationDialog } from './components/ConfirmationDialogs';
import { useAuth } from './hooks/useAuth';
import { usePosts } from './hooks/usePosts';
import { useProfile } from './hooks/useProfile';
import { useFollows } from './hooks/useFollows';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useArticles } from './hooks/useArticles';
import { useProducts } from './hooks/useProducts';
import { useExpertProfiles } from './hooks/useExpertProfiles';
import { useExpertSessions } from './hooks/useExpertSessions';
import { stateManager, persistentState } from './utils/stateManager';
import { showToast } from './utils/toast';
import { Home, User } from 'lucide-react';
import { 
  CartItem,
  Order,
  SearchSuggestion
} from './types';
import { NyansaButton } from './components/Nyansa';

function App() {
  // Network status monitoring
  const { isOnline } = useNetworkStatus();
  
  // Initialize with persistent navigation state
  const [activeSection, setActiveSection] = useState(() => {
    const navState = persistentState.navigation.get();
    return navState.activeSection || 'discover';
  });
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navigationData, setNavigationData] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [appReady, setAppReady] = useState(false);
  const [appInitProgress, setAppInitProgress] = useState(0);
  const [showConfirmNavigation, setShowConfirmNavigation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{section: string, data?: any} | null>(null);
  
  // Auth and backend hooks
  const { user, loading: authLoading } = useAuth();
  const { posts, loading: postsLoading, createPost, addComment, addReaction, voteOnPoll } = usePosts();
  const { profile, updateProfile } = useProfile();
  const { followUser, unfollowUser, isFollowing } = useFollows();
  
  // Data hooks
  const { articles } = useArticles();
  const { products, drugs } = useProducts();
  const { experts } = useExpertProfiles();
  const { sessions } = useExpertSessions();
  
  // Local storage for cart with state persistence
  const [cart, setCart] = useState<CartItem[]>(() => {
    return persistentState.cart.get() || [];
  });
  
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Persist cart changes
  useEffect(() => {
    persistentState.cart.set(cart);
  }, [cart]);

  // App initialization
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Start progress
        setAppInitProgress(10);
        
        // Apply saved theme
        const savedTheme = persistentState.theme.get();
        if (savedTheme.theme === 'dark') {
          document.documentElement.classList.add('dark');
        }
        
        setAppInitProgress(30);
        
        // Apply accent color
        if (savedTheme.accentColor) {
          const colorMap = {
            green: '#16a34a',
            blue: '#3b82f6',
            purple: '#8b5cf6',
            orange: '#f59e0b',
            red: '#ef4444',
            pink: '#ec4899'
          };
          const root = document.documentElement;
          root.style.setProperty('--color-primary', colorMap[savedTheme.accentColor as keyof typeof colorMap]);
        }
        
        setAppInitProgress(50);
        
        // Wait for auth to initialize
        await new Promise(resolve => {
          const checkAuth = () => {
            if (!authLoading) {
              resolve(true);
            } else {
              setTimeout(checkAuth, 100);
            }
          };
          checkAuth();
        });
        
        setAppInitProgress(80);
        
        // Final initialization steps
        setTimeout(() => {
          setAppInitProgress(100);
          setAppReady(true);
        }, 500);
      } catch (error) {
        console.error('App initialization error:', error);
        showToast.system.error('Failed to initialize application. Please refresh the page.');
        setAppReady(true); // Still show app even if initialization fails
      }
    };

    initializeApp();
  }, [authLoading]);

  // Persist navigation state changes
  useEffect(() => {
    persistentState.navigation.set({ activeSection });
  }, [activeSection]);

  // Auto-redirect authenticated users from landing page
  useEffect(() => {
    if (user && activeSection === 'landing') {
      setActiveSection('discover');
    }
  }, [user, activeSection]);

  // Handle unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle authentication required actions with nudging
  const handleAuthRequired = (message: string, mode: 'signin' | 'signup' = 'signin') => {
    showToast.auth.authRequired(message);
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Show loading state during initialization
  if (!appReady || authLoading) {
    return (
      <ErrorBoundary>
        <ToastProvider>
          <LoadingFallback 
            message="Initializing PharmPlus..." 
            showProgress={true}
            progress={appInitProgress}
            showTips={true}
          />
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  // Event handlers
  const handleSearch = (query: string) => {
    console.log('Searching for:', query);
    showToast.system.info(`Searching for "${query}"`);
  };

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    console.log('Selected suggestion:', suggestion);
    showToast.system.success(`Navigating to ${suggestion.category}: ${suggestion.text}`);
    
    if (suggestion.type === 'article') setActiveSection('discover');
    if (suggestion.type === 'expert') setActiveSection('spotlight');
    if (suggestion.type === 'community') setActiveSection('community');
  };

  const handleNavigate = (section: string, data?: any) => {
    // Check if we need to confirm navigation due to unsaved changes
    if (hasUnsavedChanges && section !== activeSection) {
      setPendingNavigation({ section, data });
      setShowConfirmNavigation(true);
      return;
    }
    
    // Handle navigation to different sections
    setActiveSection(section);
    setNavigationData(data);
    console.log('Navigating to:', section, 'with data:', data);
    
    // Handle specific navigation flows
    if (data) {
      if (data.openPostDetails && section === 'community') {
        console.log('Opening post details for post:', data.postId);
        showToast.system.info('Loading discussion...');
      }
      if (data.openArticleView && section === 'discover') {
        console.log('Opening article view for article:', data.articleId);
        showToast.system.info('Loading article...');
      }
      if (data.openLiveEventPage && section === 'spotlight') {
        console.log('Opening live event page for session:', data.sessionId);
        showToast.system.info('Preparing live session...');
      }
      if (data.filterResults && section === 'discover') {
        console.log('Filtering discover results with query:', data.searchQuery);
        showToast.system.info(`Showing results for "${data.searchQuery}"`);
      }
    }
  };

  const confirmNavigation = () => {
    if (pendingNavigation) {
      setActiveSection(pendingNavigation.section);
      setNavigationData(pendingNavigation.data);
      setPendingNavigation(null);
      setHasUnsavedChanges(false);
    }
    setShowConfirmNavigation(false);
  };

  const cancelNavigation = () => {
    setPendingNavigation(null);
    setShowConfirmNavigation(false);
  };

  const handlePostInteraction = async (postId: string, action: string) => {
    if (!user) {
      handleAuthRequired('Please sign in to interact with posts', 'signin');
      return;
    }

    try {
      if (action === 'like' || action === 'helpful' || action === 'heart' || action === 'thanks') {
        await addReaction(postId, action);
        showToast.community.reactionAdded(action);
      } else if (action.startsWith('vote:')) {
        // Extract option index from action string (format: "vote:0", "vote:1", etc.)
        const optionIndex = parseInt(action.split(':')[1]);
        if (!isNaN(optionIndex)) {
          await voteOnPoll(postId, optionIndex);
          showToast.community.pollVoteAdded();
        }
      }
    } catch (error) {
      showToast.system.error('Failed to process your interaction. Please try again.');
    }
  };

  const handleCreatePost = async (
    content: string, 
    category: string, 
    isAnonymous: boolean, 
    postType?: string, 
    expertTag?: string,
    pollOptions: string[] = []
  ) => {
    if (!user) {
      handleAuthRequired('Please sign in to create a post', 'signup');
      return;
    }

    try {
      const tags = expertTag ? [expertTag] : [];
      
      const createPostPromise = createPost(content, category, isAnonymous, tags, postType as any, pollOptions);
      
      showToast.community.postCreating();
      await createPostPromise;
      showToast.community.postCreated();
    } catch (error) {
      showToast.system.error('Failed to create post. Please try again.');
    }
  };

  const handleFollowExpert = async (expertUserId: string) => {
    if (!user) {
      handleAuthRequired('Please sign in to follow experts', 'signup');
      return;
    }

    const expert = experts.find(e => e.userId === expertUserId);
    const expertName = expert ? `Dr. Expert ${expert.id}` : 'Expert';
    
    try {
      if (isFollowing(expertUserId)) {
        await unfollowUser(expertUserId, expertName);
        showToast.community.unfollowSuccess(expertName);
      } else {
        await followUser(expertUserId, expertName);
        showToast.community.followSuccess(expertName);
      }
    } catch (error) {
      showToast.system.error('Failed to update follow status. Please try again.');
    }
  };

  const handleJoinSession = (sessionId: string) => {
    if (!user) {
      handleAuthRequired('Please sign in to join expert sessions', 'signup');
      return;
    }

    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      showToast.expert.sessionJoined(session.title);
    }
  };

  const handleAskQuestion = (expertId: string, question: string) => {
    if (!user) {
      handleAuthRequired('Please sign in to ask questions', 'signup');
      return;
    }

    showToast.expert.questionSubmitted();
  };

  const handleAddToCart = (productId: string, quantity: number) => {
    if (!user) {
      handleAuthRequired('Please sign in to add items to your cart', 'signup');
      return;
    }

    try {
      const product = products.find(p => p.id === productId);
      if (!product) {
        showToast.system.error('Product not found');
        return;
      }
      
      if (!product.inStock) {
        showToast.shop.outOfStock(product.name);
        return;
      }
      
      if (product.prescriptionRequired) {
        const cartItem = cart.find(item => item.productId === productId);
        if (!cartItem?.prescriptionUploaded) {
          showToast.shop.prescriptionRequired();
          return;
        }
      }

      const existingItem = cart.find(item => item.productId === productId);
      if (existingItem) {
        setCart(prev => prev.map(item =>
          item.productId === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        ));
      } else {
        const newItem: CartItem = {
          productId,
          product,
          quantity,
          prescriptionUploaded: false
        };
        setCart(prev => [...prev, newItem]);
      }
      
      showToast.shop.addedToCart(product.name);
    } catch (error) {
      showToast.system.error('Failed to add item to cart. Please try again.');
    }
  };

  const handleUpdateCart = (productId: string, quantity: number) => {
    if (!user) {
      handleAuthRequired('Please sign in to update your cart', 'signin');
      return;
    }

    try {
      if (quantity <= 0) {
        handleRemoveFromCart(productId);
        return;
      }
      
      setCart(prev => prev.map(item =>
        item.productId === productId
          ? { ...item, quantity }
          : item
      ));
      
      showToast.shop.cartUpdated();
    } catch (error) {
      showToast.system.error('Failed to update cart. Please try again.');
    }
  };

  const handleRemoveFromCart = (productId: string) => {
    if (!user) {
      handleAuthRequired('Please sign in to manage your cart', 'signin');
      return;
    }

    try {
      const product = cart.find(item => item.productId === productId)?.product;
      if (product) {
        setCart(prev => prev.filter(item => item.productId !== productId));
        showToast.shop.removedFromCart(product.name);
      }
    } catch (error) {
      showToast.system.error('Failed to remove item from cart. Please try again.');
    }
  };

  const handleCheckout = (order: Omit<Order, 'id'>) => {
    if (!user) {
      handleAuthRequired('Please sign in to complete your purchase', 'signup');
      return;
    }

    try {
      console.log('Processing order:', order);
      showToast.shop.orderPlaced('ORD-' + Date.now().toString().slice(-6));
      setCart([]);
    } catch (error) {
      showToast.system.error('Failed to process your order. Please try again.');
    }
  };

  // Sign In Required Component
  const SignInRequired = ({ title, description, icon: Icon, color }: any) => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
      <div className="text-center p-8 animate-fade-in">
        <div className={`w-16 h-16 ${color} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <Icon className="text-white" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{description}</p>
        <div className="space-y-3">
          <button
            onClick={() => {
              setAuthMode('signin');
              setShowAuthModal(true);
            }}
            className="btn-primary block mx-auto"
          >
            Sign In
          </button>
          <button
            onClick={() => {
              setAuthMode('signup');
              setShowAuthModal(true);
            }}
            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 text-sm"
          >
            Don't have an account? Sign up
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'landing':
        return (
          <LandingPage
            onGetStarted={() => {
              setAuthMode('signup');
              setShowAuthModal(true);
            }}
            onSignIn={() => {
              setAuthMode('signin');
              setShowAuthModal(true);
            }}
          />
        );
      case 'hub':
        // Private section - only for authenticated users
        if (!user) {
          return (
            <SignInRequired
              title="Sign In Required"
              description="Please sign in to access your personalized health hub"
              icon={Home}
              color="bg-green-500"
            />
          );
        }
        return (
          <Suspense fallback={<LoadingFallback message="Loading your health hub..." />}>
            <Hub
              posts={posts.map(post => ({
                id: post.id,
                authorId: post.author?.id || '',
                authorName: post.is_anonymous ? 'Anonymous' : (post.author?.full_name || 'Unknown'),
                isExpert: post.author?.is_expert || false,
                content: post.content,
                category: post.category,
                tags: post.tags,
                timestamp: new Date(post.created_at).toLocaleString(),
                reactions: post.reactions,
                comments: post.comments.map(comment => ({
                  id: comment.id,
                  postId: post.id,
                  authorId: comment.author?.id || '',
                  authorName: comment.author?.full_name || 'Unknown',
                  isExpert: comment.author?.is_expert || false,
                  content: comment.content,
                  timestamp: new Date(comment.created_at).toLocaleString(),
                  reactions: [],
                  isExpertAnswer: comment.is_expert_answer
                })),
                isAnonymous: post.is_anonymous,
                postType: post.post_type,
                pollOptions: post.poll_options,
                pollVotesCount: post.poll_votes_count,
                userVote: post.user_vote
              }))}
              articles={articles}
              upcomingSessions={sessions.filter(s => s.status === 'upcoming')}
              onPostInteraction={handlePostInteraction}
              onSearch={handleSearch}
              onNavigate={handleNavigate}
            />
          </Suspense>
        );
      case 'community':
        return (
          <Suspense fallback={<LoadingFallback message="Loading community discussions..." />}>
            <CommunityEchoes
              posts={posts.map(post => ({
                id: post.id,
                authorId: post.author?.id || '',
                authorName: post.is_anonymous ? 'Anonymous' : (post.author?.full_name || 'Unknown'),
                isExpert: post.author?.is_expert || false,
                content: post.content,
                category: post.category,
                tags: post.tags,
                timestamp: new Date(post.created_at).toLocaleString(),
                reactions: post.reactions,
                comments: post.comments.map(comment => ({
                  id: comment.id,
                  postId: post.id,
                  authorId: comment.author?.id || '',
                  authorName: comment.author?.full_name || 'Unknown',
                  isExpert: comment.author?.is_expert || false,
                  content: comment.content,
                  timestamp: new Date(comment.created_at).toLocaleString(),
                  reactions: [],
                  isExpertAnswer: comment.is_expert_answer
                })),
                isAnonymous: post.is_anonymous,
                postType: post.post_type,
                pollOptions: post.poll_options,
                pollVotesCount: post.poll_votes_count,
                userVote: post.user_vote
              }))}
              onCreatePost={handleCreatePost}
              onPostInteraction={handlePostInteraction}
              onFollowExpert={handleFollowExpert}
              navigationData={navigationData}
              currentUser={user}
              handleAuthRequired={handleAuthRequired}
            />
          </Suspense>
        );
      case 'discover':
        return (
          <Suspense fallback={<LoadingFallback message="Loading health knowledge..." />}>
            <Discover
              articles={articles}
              drugs={drugs}
              onNavigate={handleNavigate}
              navigationData={navigationData}
              currentUser={user}
              handleAuthRequired={handleAuthRequired}
            />
          </Suspense>
        );
      case 'spotlight':
        return (
          <Suspense fallback={<LoadingFallback message="Loading expert directory..." />}>
            <ExpertSpotlight
              sessions={sessions}
              experts={experts}
              onJoinSession={handleJoinSession}
              onFollowExpert={handleFollowExpert}
              onAskQuestion={handleAskQuestion}
            />
          </Suspense>
        );
      case 'shop':
        return (
          <Suspense fallback={<LoadingFallback message="Loading health products..." />}>
            <Shop
              products={products}
              cart={cart}
              onAddToCart={handleAddToCart}
              onUpdateCart={handleUpdateCart}
              onRemoveFromCart={handleRemoveFromCart}
              onCheckout={handleCheckout}
              currentUser={user}
              handleAuthRequired={handleAuthRequired}
              onNavigate={handleNavigate}
            />
          </Suspense>
        );
      case 'profile':
        // Private section - only for authenticated users
        if (!user) {
          return (
            <SignInRequired
              title="Sign In Required"
              description="Please sign in to access your profile and settings"
              icon={User}
              color="bg-purple-500"
            />
          );
        }
        return (
          <Suspense fallback={<LoadingFallback message="Loading your profile..." />}>
            <Profile
              onNavigate={handleNavigate}
              setHasUnsavedChanges={setHasUnsavedChanges}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<LoadingFallback message="Loading health knowledge..." />}>
            <Discover
              articles={articles}
              drugs={drugs}
              onNavigate={handleNavigate}
              navigationData={navigationData}
              currentUser={user}
              handleAuthRequired={handleAuthRequired}
            />
          </Suspense>
        );
    }
  };

  return (
    <ErrorBoundary>
      <ToastProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
          {/* Network Status Bar */}
          <NetworkStatusBar />
          
          <div className="flex">
            {/* Navigation is always visible except on landing page */}
            {activeSection !== 'landing' && (
              <Navigation
                activeSection={activeSection}
                onSectionChange={handleNavigate}
                isMobileMenuOpen={isMobileMenuOpen}
                onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                currentUser={user}
                setShowAuthModal={setShowAuthModal}
                setAuthMode={setAuthMode}
              />
            )}
            
            {/* Main content area */}
            <main className={`flex-1 ${activeSection !== 'landing' ? 'lg:ml-72' : ''}`}>
              <ErrorBoundary fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Something went wrong</h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">Please refresh the page to continue</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-primary"
                    >
                      Refresh Page
                    </button>
                  </div>
                </div>
              }>
                {renderContent()}
              </ErrorBoundary>
            </main>
          </div>
        </div>
        
        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onModeChange={setAuthMode}
        />
        
        {/* Navigation Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showConfirmNavigation}
          title="Unsaved Changes"
          message="You have unsaved changes that will be lost if you navigate away. Do you want to continue?"
          confirmLabel="Leave Page"
          cancelLabel="Stay on Page"
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
          type="warning"
        />

        {/* Nyansa AI Co-Pilot */}
        <NyansaButton 
          currentSection={activeSection}
          userName={profile?.full_name || user?.user_metadata?.full_name || 'there'}
          isAuthenticated={!!user}
        />
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;